import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarDays, ChevronDown, KeyRound, Landmark, Loader2, LogOut, Receipt } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fmtIsk } from "@/lib/price";

const monthLabel = (m) => {
  if (m.label) return m.label; // backend provides e.g. "Nov–Dec 2026" (two-month filing period)
  try {
    return new Date(`${m.month}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  } catch {
    return m.month;
  }
};

// Monthly VAT position: what client invoices charged vs what vendor invoices give back in credit.
export default function OwnerVat() {
  const navigate = useNavigate();
  const token = localStorage.getItem("er-owner-token") || "";
  const headers = { Authorization: `Bearer ${token}` };
  const [report, setReport] = useState(null);
  const [openMonth, setOpenMonth] = useState("");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/owner/vat-report`, { headers })
      .then((res) => setReport(res.data))
      .catch(() => {
        setReport({ months: [], charged_isk: 0, credit_isk: 0, difference_isk: 0 });
        toast.error("Could not load the VAT report.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <div data-testid="vat-locked" className="flex min-h-screen flex-col items-center justify-center bg-[#070709] px-5 text-center">
        <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto mix-blend-screen" />
        <p className="mt-8 font-serif text-3xl font-light text-neutral-200">The private door is closed.</p>
        <Link
          to="/owner"
          data-testid="vat-goto-login"
          className="mt-8 flex items-center gap-2 bg-amber-500 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
        >
          <KeyRound className="h-4 w-4" /> Go to Sign-In
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="vat-report-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <header className="sticky top-0 z-40 border-b border-amber-500/15 bg-[#0c0c10]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo.png" alt="" className="h-10 w-auto mix-blend-screen" />
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Concierge Desk</p>
              <h1 className="font-serif text-xl font-light text-neutral-50">VAT Report</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/owner/invoices" data-testid="vat-goto-invoices" className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
              <Receipt className="h-3.5 w-3.5" /> Invoices
            </Link>
            <Link to="/owner/calendar" data-testid="vat-goto-calendar" className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
              <CalendarDays className="h-3.5 w-3.5" /> Calendar
            </Link>
            <Link to="/owner/trips" data-testid="vat-goto-trips" className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200">
              Trip Builder
            </Link>
            <button
              data-testid="vat-logout-button"
              onClick={() => {
                localStorage.removeItem("er-owner-token");
                navigate("/owner");
              }}
              className="flex items-center gap-2 border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-red-400 hover:text-red-300"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 py-10">
        <p className="text-xs leading-relaxed text-neutral-500">
          Charged VAT is grouped by each client invoice's issue date, credit VAT by the date you attached the vendor invoice — both into Iceland's two-month filing periods (Jan–Feb, Mar–Apr and so on). Tap a period to see what is inside.
        </p>

        {report === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        ) : (
          <>
            <div data-testid="vat-grand-total" className="mt-6 flex flex-wrap items-center justify-between gap-3 border border-amber-500/30 bg-amber-500/5 px-4 py-3">
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200/90">
                <Landmark className="h-3.5 w-3.5" /> All months — left to pay the tax office
              </p>
              <p className="font-luxe text-xl tracking-wide text-amber-100">
                <span data-testid="vat-total-charged" className="mr-4 text-sm text-neutral-400">{fmtIsk(report.charged_isk)} charged</span>
                <span data-testid="vat-total-credit" className="mr-4 text-sm text-emerald-200/80">− {fmtIsk(report.credit_isk)}</span>
                <span data-testid="vat-total-difference">{fmtIsk(report.difference_isk)}</span>
              </p>
            </div>

            {report.months.length === 0 ? (
              <div data-testid="vat-empty-state" className="mt-6 border border-dashed border-neutral-700 py-16 text-center">
                <p className="font-serif text-2xl font-light text-neutral-400">Nothing to report yet.</p>
                <p className="mt-2 text-sm text-neutral-600">Save a client invoice or attach a vendor invoice and the months appear here.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-3">
                {report.months.map((m) => (
                  <div key={m.month} data-testid={`vat-row-${m.month}`} className="border border-amber-500/15 bg-[#14141a]">
                    <button
                      data-testid={`vat-month-toggle-${m.month}`}
                      onClick={() => setOpenMonth((o) => (o === m.month ? "" : m.month))}
                      aria-expanded={openMonth === m.month}
                      className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-amber-500/5"
                    >
                      <span className="flex items-center gap-2 font-serif text-lg font-light text-neutral-50">
                        <ChevronDown className={`h-4 w-4 text-amber-300/70 transition-transform ${openMonth === m.month ? "rotate-180" : ""}`} />
                        {monthLabel(m)}
                      </span>
                      <span className="flex flex-wrap items-center gap-4 text-xs">
                        <span data-testid={`vat-charged-${m.month}`} className="text-neutral-400">
                          Charged <span className="ml-1 font-luxe tracking-wide text-amber-100">{fmtIsk(m.charged_isk)}</span>
                        </span>
                        <span data-testid={`vat-credit-${m.month}`} className="text-neutral-400">
                          Credit <span className="ml-1 font-luxe tracking-wide text-emerald-200">− {fmtIsk(m.credit_isk)}</span>
                        </span>
                        <span data-testid={`vat-difference-${m.month}`} className="text-neutral-300">
                          To pay <span className="ml-1 font-luxe tracking-wide text-amber-200">{fmtIsk(m.difference_isk)}</span>
                        </span>
                      </span>
                    </button>
                    {openMonth === m.month && (
                      <div data-testid={`vat-detail-${m.month}`} className="grid gap-4 border-t border-amber-500/10 px-5 py-4 sm:grid-cols-2">
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Charged to clients</p>
                          {m.charged_detail.length === 0 && <p className="mt-2 text-xs text-neutral-600">No client invoices this month.</p>}
                          <div className="mt-2 space-y-1">
                            {m.charged_detail.map((d, i) => (
                              <p key={i} data-testid={`vat-charged-line-${m.month}-${i}`} className="flex items-center justify-between gap-3 text-xs text-neutral-300">
                                <span className="truncate">{d.label}</span>
                                <span className="shrink-0 font-luxe tracking-wide text-amber-100/90">{fmtIsk(d.amount_isk)}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Credit from vendor invoices</p>
                          {m.credit_detail.length === 0 && <p className="mt-2 text-xs text-neutral-600">No vendor invoices attached this month.</p>}
                          <div className="mt-2 space-y-1">
                            {m.credit_detail.map((d, i) => (
                              <p key={i} data-testid={`vat-credit-line-${m.month}-${i}`} className="flex items-center justify-between gap-3 text-xs text-neutral-300">
                                <span className="truncate">{d.label}</span>
                                <span className="shrink-0 font-luxe tracking-wide text-emerald-200/90">{fmtIsk(d.amount_isk)}</span>
                              </p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
